import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Circle, Hammer, ListTodo, X } from 'lucide-react';

export default function AIAgentPlanCard({
  plan,
  onBuild,
  onCancel,
  lightSurface = false,
  isBuilding = false,
}) {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.shared.aiAgent.planCard.${key}`, opts);

  if (!plan || plan.status !== 'ready' || !(plan.steps?.length > 0)) return null;

  const shellClass = lightSurface
    ? 'border-white/40 bg-white/75 backdrop-blur-md'
    : 'border-white/20 bg-white/10 backdrop-blur-lg';

  const textClass = lightSurface ? 'text-gray-900' : 'text-white';
  const mutedClass = lightSurface ? 'text-gray-600' : 'text-white/60';
  const stepClass = lightSurface
    ? 'bg-white/60 border-white/40'
    : 'bg-white/[0.06] border-white/15';

  return (
    <div
      className={`w-full max-w-[640px] rounded-2xl border p-4 ${shellClass}`}
      role="region"
      aria-label={p('aria_region')}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wide ${mutedClass}`}>
            <ListTodo size={14} />
            {p('title')}
          </div>
          <h3 className={`m-0 mt-1 text-lg font-bold ${textClass}`}>{plan.title}</h3>
          {plan.objective ? (
            <p className={`m-0 mt-1 text-sm leading-relaxed ${mutedClass}`}>{plan.objective}</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onCancel}
          disabled={isBuilding}
          className={`shrink-0 p-1 rounded-md border-none bg-transparent cursor-pointer ${
            lightSurface ? 'text-gray-400 hover:text-gray-700' : 'text-white/50 hover:text-white'
          }`}
          aria-label={p('close_aria')}
        >
          <X size={16} />
        </button>
      </div>

      {plan.prerequisites?.length > 0 && (
        <div className="mb-3">
          <p className={`m-0 mb-1 text-xs font-semibold uppercase ${mutedClass}`}>{p('prerequisites')}</p>
          <ul className={`m-0 pl-4 text-sm space-y-0.5 ${mutedClass}`}>
            {plan.prerequisites.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-3">
        <p className={`m-0 mb-2 text-xs font-semibold uppercase ${mutedClass}`}>
          {p('steps_count', { count: plan.steps?.length || 0 })}
        </p>
        {(plan.steps?.length || 0) === 0 ? (
          <p className={`m-0 text-sm ${mutedClass}`}>{p('no_steps')}</p>
        ) : (
          <ol className="m-0 p-0 list-none space-y-2">
            {(plan.steps || []).map((step, index) => (
            <li
              key={step.id || index}
              className={`flex gap-2.5 p-2.5 rounded-xl border ${stepClass}`}
            >
              <Circle size={14} className={`shrink-0 mt-0.5 ${mutedClass}`} />
              <div className="min-w-0 flex-1">
                <p className={`m-0 text-sm font-semibold ${textClass}`}>
                  {index + 1}. {step.title}
                </p>
                {step.detail ? (
                  <p className={`m-0 mt-0.5 text-xs leading-relaxed ${mutedClass}`}>
                    {step.detail}
                  </p>
                ) : null}
                {step.needs_confirmation ? (
                  <span
                    className={`inline-block mt-1 text-[0.62rem] font-medium px-1.5 py-0.5 rounded ${
                      lightSurface
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-amber-500/15 text-amber-200'
                    }`}
                  >
                    {p('confirmation_required')}
                  </span>
                ) : null}
              </div>
            </li>
            ))}
          </ol>
        )}
      </div>

      {plan.risks?.length > 0 && (
        <div className="mb-4">
          <p className={`m-0 mb-1 text-xs font-semibold uppercase ${mutedClass}`}>
            {p('attention_points')}
          </p>
          <ul className={`m-0 pl-4 text-xs space-y-0.5 ${mutedClass}`}>
            {plan.risks.map((risk) => (
              <li key={risk}>{risk}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onBuild}
          disabled={isBuilding || !(plan.steps?.length > 0)}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border-none text-sm font-semibold cursor-pointer bg-gradient-to-r from-amber-400 to-amber-500 text-slate-900 hover:from-amber-300 hover:to-amber-400 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-amber-500/20"
        >
          <Hammer size={15} />
          {isBuilding ? p('executing') : p('execute_plan')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isBuilding}
          className={`px-3 py-2 rounded-xl border text-sm cursor-pointer ${
            lightSurface
              ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              : 'border-white/20 bg-transparent text-white/80 hover:bg-white/5'
          }`}
        >
          {p('cancel')}
        </button>
      </div>

      <p className={`m-0 mt-3 text-[0.65rem] flex items-center gap-1 ${mutedClass}`}>
        <CheckCircle2 size={12} />
        {p('agent_mode_hint')}
      </p>
    </div>
  );
}
